import L from 'leaflet';
import { useEffect, useRef, useMemo } from 'react';
import { Marker } from 'react-leaflet';

import { MarkerProvider } from '@/hooks/MarkerContext';
import { usePhotos } from '@/hooks/usePhotos';
import { mergeClasses } from '@/lib/utils';
import { Photo } from '@/lib/Photo';
import { Photos } from '@/lib/Photos';

const WrappedMarker = ({ id, isMarkerForRoute, markerAction = () => null, position, ...props }: {
  id: string,
  isMarkerForRoute: boolean,
  markerAction: () => void,
  position: [number, number],
  [key: string]: any
}) => {
  const markerRef = useRef<any>(null);
  const { photos: photoData } = usePhotos();
  
  // Convert PhotoData to Photo instances
  const photoInstances = useMemo(() => photoData.map(data => Photo.fromApi(data)), [photoData]);
  const photos = useMemo(() => new Photos(photoInstances), [photoInstances]);
  const actions = MarkerProvider.useMarkerContext();

  const isOnTop = actions.markerOnTopId === id;
  const photo = photos.getPhotoById(id);
  const marker = markerRef.current;

  useEffect(() => {
    if (marker) {
      actions.registerMarker(id, marker);
    }
  }, [id, marker, actions]);

  useEffect(() => {
    if (isMarkerForRoute) {
      actions.setMarkerOnTopId(prev => prev === null ? id : prev);
    }
  }, [isMarkerForRoute, id, actions]);

  if (!photo) {
    console.log('omg panic lol', id);
    return null;
  }

  const imageIcon = ({ url, size, className }: { url: string, size: number, className: string }) => {
    return new L.DivIcon({
      html: `<img src="${url}" alt="Photo" class="!w-full !h-full object-cover" />`,
      className: mergeClasses('p-1 bg-slate-100 rounded-sm', className),
      iconSize: [size, size],
    });
  }

  const normalImageIcon = (markerId: string) => {
    const photo = photos.getPhotoById(markerId);
    const url = photo?.imageUrl ?? 'https://placehold.co/400';

    return imageIcon({ url, size: 100, className: 'border-2 border-slate-400' });
  };

  const onTopImageIcon = (markerId: string) => {
    const photo = photos.getPhotoById(markerId);
    const url = photo?.imageUrl ?? 'https://placehold.co/400';

    return imageIcon({ url, size: 120, className: 'border-2 border-slate-600' });
  };

  // TODO this isn't fully correct; if a>b>c, clicking b will go b>a>c, but then
  // clicking c will go c>a>b (a,b will reset to default), not c>b>a (which we want)
  const onTopify = (markerId: string) => {
    actions.updateMarker(markerId, { icon: onTopImageIcon(markerId), zIndexOffset: 1000 });
  };

  const offTopify = (markerId: string | null) => {
    if (!markerId) return;
    actions.updateMarker(markerId, { icon: normalImageIcon(markerId), zIndexOffset: 0 });
  };

  const onClick = (id: string, actions: any) => {
    console.log('onClick', id, actions);
    if (isOnTop) {
      markerAction();
    } else {
      onTopify(id);
      actions.setMarkerOnTopId((prev: string | null) => {
        offTopify(prev);
        return id;
      });
    }
  }

  const defaultIcon = isOnTop ? onTopImageIcon(id) : normalImageIcon(id);
  const defaultZIndexOffset = isOnTop ? 1000 : 0;

  const mergedProps = {
    icon: defaultIcon,
    zIndexOffset: defaultZIndexOffset,
    ...props,
    ref: markerRef,
    eventHandlers: {
      click: () => { onClick(id, actions); }
    }
  };

  return <Marker key={id} position={position} {...mergedProps} />;
};

export default WrappedMarker;
